import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { CheckCircleIcon } from '@phosphor-icons/react';
import RightPanelShell from 'src/components/layouts/RightPanelShell';
import QuickTips from './QuickTips';
import WikiGuide from './WikiGuide';
import ReviewGuide from './ReviewGuide';
import DashboardGuide from './DashboardGuide';
import ChatGuide from './ChatGuide';
import WritingGuide from './WritingGuide';
import DataLibraryGuide from './DataLibraryGuide';
import { useRightPanelControl } from '../../../hooks/useRightPanelControl';
import useAuth from 'src/hooks/useAuth';
import useActiveTenantRole from 'src/hooks/useActiveTenantRole';
import ProgressBar from 'src/components/ui/ProgressBar';
import { ONBOARDING_MODULES, OnboardingModule, ModuleKey, isOnboardingModuleCompleted } from './onboarding';
import { isOrganisationTenantAtPath } from 'src/lib/tenantRouting';

interface OnboardingContentProps {
  onComplete?: () => void;
}

const OnboardingContent: React.FC<OnboardingContentProps> = () => {
  const { setContent } = useRightPanelControl();
  const navigate = useTenantNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { role: activeTenantRole } = useActiveTenantRole();
  const activeTenant = user?.getActiveTenant?.(location.pathname);
  const isModuleCompleted = (key: ModuleKey) => isOnboardingModuleCompleted(key);
  const modules: OnboardingModule[] = ONBOARDING_MODULES;
  const [, setActiveModule] = useState<ModuleKey>('welcome');
  const [viewMode, setViewMode] = useState<'overview' | 'module'>('overview');

  const navigateToModule = (m: OnboardingModule) => {
    if (m.key === 'welcome') {
      setContent(<QuickTips />);
      return;
    }
    if (m.key === 'chat') {
      setContent(<ChatGuide />);
      return;
    }
    if (m.key === 'wiki') {
      setContent(<WikiGuide />);
      return;
    }
    if (m.key === 'review') {
      setContent(<ReviewGuide />);
      return;
    }
    if (m.key === 'dashboard') {
      setContent(<DashboardGuide />);
      return;
    }
    if (m.key === 'write') {
      setContent(<WritingGuide />);
      return;
    }
    if (m.key === 'dataLibrary') {
      setContent(<DataLibraryGuide />);
      return;
    }
    if (m.route) navigate(m.route);
    setActiveModule(m.key);
    setViewMode('module');
  };

  const hasDataLibraryAccess =
    isOrganisationTenantAtPath(location.pathname, user?.tenants) || activeTenant?.features?.includes('search_index');

  const isModuleVisible = (m: OnboardingModule) => {
    if (m.key === 'dataLibrary') return hasDataLibraryAccess;
    if (m.requiresChampion) return activeTenantRole === 'champion' || activeTenantRole === 'admin';
    return true;
  };

  useEffect(() => {
    if (location.pathname.includes('/profile') || location.pathname.includes('/settings')) {
      localStorage.setItem('innex-profile-visited', 'true');
    }
    if (location.pathname.startsWith('/dashboard')) localStorage.setItem('innex-dashboard-visited', 'true');
  }, [location.pathname]);

  return (
    <RightPanelShell title="Onboarding">
      <div className="h-full flex flex-col space-y-6">
        {viewMode === 'overview' ? (
          <div className="flex flex-col gap-2">
            {modules.filter(isModuleVisible).map((m) => {
              const completed = isModuleCompleted(m.key);
              const minutes = m.minutes ?? 3;
              return (
                <div key={m.key} className="border border-border-default rounded-md bg-white">
                  <button
                    className={`w-full flex items-center justify-between px-3 py-2 ${completed ? 'opacity-40' : ''}`}
                    onClick={() => navigateToModule(m)}
                  >
                    <div className="flex items-center gap-3 text-left">
                      {completed ? (
                        <CheckCircleIcon size={18} className="text-green-600" />
                      ) : (
                        <span className="w-[18px] h-[18px] rounded-full border border-border-default inline-block" />
                      )}
                      <span className={`text-sm ${completed ? 'text-gray-900' : ''}`}>{m.title}</span>
                    </div>
                    <span className={`text-xs text-content-subtle`}>{`${minutes} min`}</span>
                  </button>
                </div>
              );
            })}

            {/* Overview progress */}
            <div className="pt-2">
              {(() => {
                const visibleModules = modules.filter(isModuleVisible);
                const completedCount = visibleModules.filter((m) => isModuleCompleted(m.key)).length;
                const percent = Math.round((completedCount / visibleModules.length) * 100);
                const allDone = completedCount === visibleModules.length && visibleModules.length > 0;
                return (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between text-xs text-content-subtle">
                      <span>
                        {completedCount} of {visibleModules.length} completed
                      </span>
                      {allDone && <span className="text-green-700 font-medium">Onboarding Completed</span>}
                    </div>
                    <ProgressBar value={percent} variant={allDone ? 'success' : 'in-progress'} />
                  </div>
                );
              })()}
            </div>
            <p className="text-xs text-content-subtle">
              For the best experience, please complete all modules. If you skip a step, you will need to complete the
              module again to mark it as completed.
            </p>
          </div>
        ) : null}
      </div>
    </RightPanelShell>
  );
};

export default OnboardingContent;
